/**
 * Derives the per-tenant Engage configuration posted to daas-engage-integration,
 * ADO 186742 "Engage-Inform User Synchronization and Merged User Creation", item 11.
 *
 * Kept out of the component and pure so the derivation can be tested on its own — the
 * component's job is to gather the inputs and make the two HTTP calls, not to decide
 * what the document says.
 *
 * Every field is derived rather than typed. Nothing here is a free-text box an admin
 * could disagree with the code about.
 */

/**
 * The label the Engage push writes its user nodes under.
 *
 * Mirrors `ENGAGE_USER_LABEL` in @whistle/helper-functions, which whistle-main cannot
 * import — it is a backend package and this SPA depends on no @whistle packages at all.
 * If that constant ever changes, this one moves with it: the push writes nodes under
 * that label and this tells the graph where to look for them, so a disagreement means
 * the merge reads an empty side and silently produces passthrough nodes.
 */
export const ENGAGE_USER_LABEL = "One10_User";

/**
 * The property on the Engage user node holding the address the merge joins on.
 *
 * NOT the integration service's `email` default. The push sends every property under
 * its own Whistle column name, and the column carrying the work address is
 * `businessEmail`; renaming it in flight would be a second definition of the merge key.
 * Leave this unset and the merge looks for a property no Engage node carries, so every
 * user merges with nobody.
 */
export const ENGAGE_EMAIL_PROPERTY = "businessEmail";

/**
 * Invert the Inform user map for the integration service.
 *
 * The tab stores `{ engageField: informAttribute }` — that is the direction the mapping
 * table is read in, Engage property on the left. `ExternalPropertyMap` is the opposite,
 * `{ clientProperty: engageProperty }`, because it renames the CLIENT side onto Engage
 * names before the union.
 *
 * Throws on a collision rather than dropping one. Two Engage fields mapped to one Inform
 * attribute cannot both survive the inversion, and quietly keeping the last one would
 * push a configuration the admin never described. The mapping UI already prevents this,
 * so reaching here means something upstream changed.
 *
 * @param {Object<string,string>} informUserMap
 * @returns {Object<string,string>} `{ clientProperty: engageProperty }`
 */
export const toExternalPropertyMap = (informUserMap) => {
  const inverted = {};
  Object.entries(informUserMap || {}).forEach(([engageProperty, clientProperty]) => {
    if (!clientProperty) return;
    if (inverted[clientProperty]) {
      throw new Error(
        `Inform attribute "${clientProperty}" is mapped to both "${inverted[clientProperty]}" and "${engageProperty}". Each attribute can map to only one Engage property.`
      );
    }
    inverted[clientProperty] = engageProperty;
  });
  return inverted;
};

/**
 * Build the configuration document to post back.
 *
 * Starts from the tenant's current configuration and overwrites only what Engage owns,
 * so `MetricLabel`, `PayoutLabel` and anything else the document grows stay exactly as
 * they were. The endpoint takes a whole document, so reading first is what stops this
 * tab from blanking another team's settings.
 *
 * `MergedUserLabel` is deliberately absent from the derived set. It defaults to
 * `Merged_User`, and the Claim mapper recognises an Engage-owned node by that prefix —
 * a renamed merged label would send merged users to the client's own label instead.
 * Whatever the tenant already has is preserved rather than cleared, because clearing a
 * value an admin set on purpose is not this tab's call.
 *
 * @param {Object} params
 * @param {Object} [params.current] The tenant's current configuration, as read back.
 * @param {string} [params.externalUserLabel] The client's participant label, blank when
 *   Engage owns this tenant's users outright.
 * @param {Object<string,string>} [params.informUserMap] `{ engageField: informAttribute }`.
 * @param {string[]} [params.allowOverrideProperties] Engage fields allowed to win a merge.
 * @returns {Object} The document to POST.
 */
export const buildEngageConfiguration = ({
  current = {},
  externalUserLabel = "",
  informUserMap = {},
  allowOverrideProperties = [],
} = {}) => {
  const trimmedExternalLabel = (externalUserLabel || "").trim();

  // Blank is a meaningful choice, not an omission: it declares that Engage owns this
  // tenant's users. With no external label there is no client side, so the rename map
  // and the external email property have nothing to describe and are cleared with it.
  if (!trimmedExternalLabel) {
    return {
      ...current,
      InternalUserLabel: ENGAGE_USER_LABEL,
      InternalEmailProperty: ENGAGE_EMAIL_PROPERTY,
      ExternalUserLabel: null,
      ExternalEmailProperty: null,
      ExternalPropertyMap: null,
      AllowUserOverrideProperties: [...allowOverrideProperties],
    };
  }

  return {
    ...current,
    InternalUserLabel: ENGAGE_USER_LABEL,
    InternalEmailProperty: ENGAGE_EMAIL_PROPERTY,
    ExternalUserLabel: trimmedExternalLabel,
    // The client property carrying the address, read off the same mapping row the admin
    // filled in for Email. Null when Email is unmapped, which the save path blocks
    // anyway — Email is a required row.
    ExternalEmailProperty: informUserMap[ENGAGE_EMAIL_PROPERTY] || null,
    ExternalPropertyMap: toExternalPropertyMap(informUserMap),
    AllowUserOverrideProperties: [...allowOverrideProperties],
  };
};

/** Null and undefined both mean "unset" here — the service omits what it has not stored. */
const sameScalar = (a, b) => (a ?? null) === (b ?? null);

/** Order carries no meaning in the override list, so compare it as a set. */
const sameList = (a, b) => {
  const left = [...(a || [])].sort();
  const right = [...(b || [])].sort();
  return left.length === right.length && left.every((value, index) => value === right[index]);
};

const sameMap = (a, b) => {
  const left = a || {};
  const right = b || {};
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every((key, index) => key === rightKeys[index] && left[key] === right[key])
  );
};

/**
 * Whether a configuration read back carries the fields we just wrote.
 *
 * The POST returns 202 and the graph write happens off a Kafka topic, so a read taken
 * straight afterwards still returns the previous document. This is what lets the save
 * path wait for its own write to land instead of assuming it did.
 *
 * Compares ONLY the fields Engage owns. Another team writing `MetricLabel` in the same
 * window is a legitimate concurrent change, not evidence that our write was lost — and
 * comparing the whole document would read it as a failure.
 *
 * @param {Object} stored The document read back.
 * @param {Object} expected The document that was posted.
 * @returns {boolean}
 */
export const engageConfigurationMatches = (stored, expected) => {
  if (!stored || !expected) return false;
  return (
    sameScalar(stored.InternalUserLabel, expected.InternalUserLabel) &&
    sameScalar(stored.InternalEmailProperty, expected.InternalEmailProperty) &&
    sameScalar(stored.ExternalUserLabel, expected.ExternalUserLabel) &&
    sameScalar(stored.ExternalEmailProperty, expected.ExternalEmailProperty) &&
    sameMap(stored.ExternalPropertyMap, expected.ExternalPropertyMap) &&
    sameList(stored.AllowUserOverrideProperties, expected.AllowUserOverrideProperties)
  );
};
